import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
  TemplateRef,
} from '@angular/core';
import { LearnerHomeTrackerService } from '@app/learner-home/services/learner-home-tracker.service';
import { QueueItem } from '@app/profile/components/profile-saved/profile-saved.component';
import { NormalizedRecommendation } from '@app/recommendations/recommendations.api';

/**
 * This component uses {@link ChunkedPipe} and {@link dgxIntersectionObserver} to lazy load an array
 * by N elements, where N is set by chunkSize. For this to work correctly,
 * the inner component to render must be wrapped with `ng-template`.
 * Also, `let-*` is required to get access to the array item in the `ng-template`.
 * Where * is the name of the variable that can be used in the `ng-template`
 *
 * See example below for setup:
 *
 * @example
 * <dgx-lazy-chunk
 *   [list]="itemsArry"
 *   [trackBy]="trackByItemId"
 *   [loadingTemplateHeight]="30"
 *   [chunkSize]="8"
 * >
 *   <ng-template let-contextItem>
 *     <dgx-profile-shared-tile
 *       class="tile__wrapper"
 *       [item]="contextItem"
 *       [actions]="tileActionsConfig"
 *     ></dgx-profile-shared-tile>
 *   </ng-template>
 * </dgx-lazy-chunk>
 */
@Component({
  selector: 'dgx-lazy-chunk',
  templateUrl: './lazy-chunk.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LazyChunkComponent {
  /** The list of items to lazy load */
  @Input() public list: any[];
  /** Trackby function passed to the *ngFor when looping over the items */
  @Input() public trackBy: (number, any) => number;
  /** Height that will be set on the loading templates in REM */
  @Input() public loadingTemplateHeight: number;
  /** Number of items that will be loaded in each chunk */
  @Input() public chunkSize?: number = 4;
  /** Object passed to the dgxIntersectionObserver options */
  @Input() public options?: IntersectionObserverInit = {};
  /** The number of cards to show per row - 3 for home, 4 for profile */
  @Input() public rowLength: number;
  @ContentChild(TemplateRef) public template: TemplateRef<any>;

  public chunkRendered: boolean[] = [];

  constructor(
    private changeDetector: ChangeDetectorRef,
    private learnerHomeTrackerService: LearnerHomeTrackerService
  ) {}
  public renderChunk(
    chunkIndex: number,
    chunk: QueueItem[] | NormalizedRecommendation[]
  ) {
    this.chunkRendered[chunkIndex] = true;
    if (chunkIndex > 0) {
      this.chunkRendered[chunkIndex - 1] = true;
    }
    this.changeDetector.markForCheck();
    chunk.forEach((listItem: QueueItem | NormalizedRecommendation) => {
      const listItemIndex = this.list.indexOf(listItem);
      this.trackEventData(listItem, listItemIndex);
    });
  }

  private trackEventData(
    listItem: QueueItem | NormalizedRecommendation,
    listItemIndex: number
  ): void {
    const properties = {
      // Conditionally update this location if this component is ever used outside of the home page
      location: 'Learner Home', // Maps to ActionLocation in Amplitude
      inputType: listItem.reference.inputType, // Maps to ContentType in Amplitude
      contentId: listItem.reference.inputId,
      title: listItem.reference.title,
      providerId: listItem.reference.providerId,
      providerName: listItem.reference.providerName,
      externalId: listItem.reference.externalId,
      position: listItemIndex,
      dateCreated: listItem.dateCreated,
    };
    this.learnerHomeTrackerService.trackContentImpressed(properties);
  }
}
